import { LitElement, html } from 'lit';

/**
 * Letterbox Services Special Terms Content
 * Renders the full legal text for Letterbox Services special terms.
 * Content matches the official PDF word-for-word.
 */
class LetterboxSpecialTerms extends LitElement {
    createRenderRoot() {
        return this;
    }

    render() {
        return html`
            <div class="special-terms">
                <h4 class="text-base font-bold mb-4">LETTERBOX SERVICES - SPECIAL TERMS</h4>

                <h5>1. ABOUT THESE TERMS</h5>

                <p>
                    1.1 These Special Terms for Letterbox Services sets out the Special Terms that
                    apply to Letterbox Services acquired by the Client through the Booking
                    Confirmation.
                </p>

                <p>
                    1.2 These Special Terms also incorporate the Distribution Requirements available
                    on the REACH website:
                    <a href="https://www.reach.nz" target="_blank" rel="noopener noreferrer"
                        >www.REACH.nz</a
                    >, as amended from time to time. REACH will endeavour to notify the Client 10
                    Business Days before any amendments to the Distribution Requirements take
                    effect.
                </p>

                <h5>2. DEFINITIONS</h5>

                <p>
                    2.1 For the purposes of these Special Terms, the following terms have the
                    following meanings:
                </p>

                <p class="ml-6">
                    (a) <strong>Distribution Requirements</strong> means the distribution
                    requirements available on the REACH website:
                    <a href="https://www.reach.nz" target="_blank" rel="noopener noreferrer"
                        >www.REACH.nz</a
                    >.
                </p>

                <p class="ml-6">
                    (b) <strong>Letterbox Services</strong> means the design, print, production,
                    distribution, booking and Product lodgement services specified in the Booking
                    Confirmation.
                </p>

                <p class="ml-6">
                    (c) <strong>Product</strong> means any catalogue, flyer, publication and/or
                    product sample provided by the Client to REACH in connection with the Letterbox
                    Services.
                </p>

                <p>
                    2.2 Terms not defined in these Special Terms have the same meaning as the Terms
                    and Conditions.
                </p>

                <h5>3. LETTERBOX SERVICES</h5>

                <p>
                    3.1 Where the Letterbox Services include physical distribution of the Product,
                    REACH will use reasonable endeavours to ensure that:
                </p>

                <p class="ml-6">
                    (a) Product is not delivered to letterboxes where a sign requesting non-delivery
                    or an "Addressed Mail Only" sign is displayed;
                </p>

                <p class="ml-6">
                    (b) Product is not delivered to addresses where there is no letterbox or where
                    the letterbox is unsuitable for receiving or holding the Product;
                </p>

                <p class="ml-6">
                    (c) Product is not delivered to letterboxes that are already full; and
                </p>

                <p class="ml-6">
                    (d) other mail/products already in the letterbox are not removed to make space
                    for the Product.
                </p>

                <p>
                    3.2 REACH will use reasonable endeavours to rectify any instances where the
                    Product is reported to be in the letterboxes referred to in clause 3.1 and
                    ensure that in future the Product is not delivered to these letterboxes.
                </p>

                <p>
                    3.3 The Letterbox Services will be performed by REACH and its subcontractors
                    using reasonable endeavours. However, the Client acknowledges and agrees that
                    the nature of the Letterbox Services and of REACH's distribution network means
                    that deliveries may vary in specific areas and that delivery to some homes or
                    businesses may not be completed. REACH does not warrant or represent that the
                    Letterbox Services will be completed in full, or that all Product will be
                    properly delivered. The pricing offered by REACH for Letterbox Services reflects
                    these variables. Where REACH becomes aware of missed or incomplete deliveries,
                    it will use reasonable endeavours to perform the Services using any available
                    spare Product, but it will not be liable to pay any compensation to the Client,
                    or for any production (or reproduction) or other costs relating to undelivered
                    Product.
                </p>

                <p>
                    3.4 The Client shall ensure that the Product is presented at the specified
                    processing locations according to the requirements set out in the Distribution
                    Requirements.
                </p>

                <p>
                    3.5 If Product logistics are not managed by REACH, it is the Client's
                    responsibility to ensure that the Product arrives at the destination(s) as
                    instructed by REACH by the date specified in the Distribution Requirements. The
                    Client acknowledges that late Product arrival could result in extra charges and
                    / or delays to the distribution.
                </p>

                <p>
                    3.6 The Client must not insert any secondary product within the Product unless
                    otherwise agreed by REACH in writing.
                </p>

                <p>3.7 REACH reserves the right to:</p>

                <p class="ml-6">
                    (a) open and inspect the Product at any time and refuse to accept or deliver the
                    Product at its reasonable discretion, including where Product includes any
                    objectionable material; and
                </p>

                <p class="ml-6">
                    (b) to reject imperfect, defective or unsuitable Product provided by the Client
                    at its discretion (including, but not limited to, when the Product breaches the
                    warranties set out in clause 9.6 of the Terms and Conditions).
                </p>

                <p>3.8 The Client acknowledges that REACH:</p>

                <p class="ml-6">
                    (a) may dispose of excess Product after seven (7) days from the last date of
                    distribution;
                </p>

                <p class="ml-6">
                    (b) will not store excess Product for longer than three (3) months from the last
                    date of distribution; and
                </p>

                <p class="ml-6">
                    (c) will charge a storage fee for excess Product stored for longer than seven
                    (7) days.
                </p>

                <h5>4. DESIGN</h5>

                <p>
                    4.1 Where the Services include design services, and the Client provides any
                    content, images, graphics or other design materials for the design (such as the
                    Client's logo) (<strong>Design Materials</strong>), the Client:
                </p>

                <p class="ml-6">
                    (a) must supply the Design Materials in the format and in accordance with any
                    requirements notified by REACH to the Client from time to time;
                </p>

                <p class="ml-6">
                    (b) must ensure the Design Materials comply with all relevant laws and
                    regulations, advertising and policy and guidelines, including the Fair Trading
                    Act 1986, applicable Advertising Standards Authority Codes, and applicable
                    platform-specific policies and terms; and
                </p>

                <p class="ml-6">
                    (c) warrants that the Design Materials do not infringe any other party's privacy
                    rights or Intellectual Property Rights.
                </p>

                <p>
                    4.2 REACH will provide a draft of the final design deliverable based on the
                    Design Materials and/or any other information and content provided by the
                    Client, for approval from the Client. The Client is responsible for reviewing
                    and verifying the final draft design (including as to accuracy of any pricing,
                    statements and other information contained in it). Once the Client has approved
                    the design, REACH will not be responsible for any defects, flaws, inaccuracies,
                    omissions or other errors in the design or finished Product. Any post-approval
                    changes or edits that need to be made will be at the Client's sole cost and
                    expense.
                </p>

                <h5>5. PRINT AND PRODUCTION</h5>

                <p>
                    5.1 The Client will be responsible for final approval of the content and design
                    for any Product before it is printed and/or produced. Once the Client's approval
                    is given, any subsequent changes or re-printing required will be at the Client's
                    sole cost and expense.
                </p>

                <h5>6. PRICING</h5>

                <p>
                    6.1 The pricing has been calculated based on information provided by the Client
                    at the time of the Agreement, in particular information concerning:
                </p>

                <p class="ml-6">(a) projected volume of services to be purchased annually;</p>

                <p class="ml-6">(b) projected frequency of distributions;</p>

                <p class="ml-6">(c) printing and transportation arrangements; and</p>

                <p class="ml-6">(d) exclusivity arrangements between REACH and the Client.</p>

                <p>
                    6.2 If the Client fails to realise any of the projected factors noted in clause
                    6.1, REACH reserves the right to review the pricing and / or recover any
                    shortfall between what was charged based on actual factors or what would have
                    been charged if actual factors had been what was projected.
                </p>
            </div>
        `;
    }
}

customElements.define('letterbox-special-terms', LetterboxSpecialTerms);
